import React from 'react';
import Chart from '../../CommonComponents/Chart';

interface Data {
  id: string;
  name: string;
  data: number[];
}

interface RetirementChartData {
  Categories: string[];
  Data: Data[];
}

interface Props {
  chartData: RetirementChartData;
  retirementAge: string;
}

const totalSavingsAtRetirementDataLabel = {
  enabled: true,
  borderRadius: 5,
  backgroundColor: 'rgba(255,255,255,0.6)',
  borderWidth: 1,
  borderColor: 'rgba(51,51,51,0.2)',
  align: 'left',
  padding: 10,
  style: {
    fontSize: '13px',
    fontFamily: 'Open Sans, helvetica, sans-serif',
    width: '100',
  },
  x: 10,
  y: 10,
  zIndex: 8,
  formatter() {
    return `Total Savings at Retirement<br/><b>$${ADV.util.number_format(this.y, 0)}</b>`;
  },
};

const RetirementChart: React.FC<Props> = ({ chartData, retirementAge }) => {
  const currentPlan = chartData.Data.find(item => item.id === 'Current') || {
    data: [],
    id: '',
    name: '',
  };
  const proposedPlan = chartData.Data.find(item => item.id === 'Proposed') || {
    data: [],
    id: '',
    name: '',
  };

  return (
    <Chart
      options={{
        chart: {
          type: 'area',
        },
        title: {
          text: '',
        },
        legend: {
          enabled: false,
        },
        credits: {
          enabled: false,
        },
        exporting: {
          enabled: false,
        },
        xAxis: {
          plotLines: [
            {
              value: chartData.Categories.indexOf(retirementAge.toString()),
              text: retirementAge,
              width: 2,
              dashStyle: 'ShortDash',
              color: '#5FBFE2',
              zIndex: 20, // Update zIndex ?
              label: {
                text: retirementAge,
                rotation: 0,
                align: 'center',
                verticalAlign: 'bottom',
                y: 22,
                style: {
                  color: '#5FBFE2',
                  fontWeight: '800',
                  fontSize: '18px',
                  fontFamily: 'Open Sans, helvetica, sans-serif',
                  zIndex: 1000,
                  textShadow:
                    '-2px -2px 0 #fff, 2px -2px 0 #fff, 2px 2px 0 #fff, 2px 2px 0 #fff, 0px 3px #fff, -3px 2px #fff, 3px 0 #fff, -5px 0 #fff, 5px 0 #fff, -8px 0 #fff, 8px 0 #fff',
                },
              },
            },
          ],
          labels: {
            style: {
              color: 'transparent',
            },
          },
          tickInterval: 5,
          tickWidth: 0,
          categories: chartData.Categories,
        },
        yAxis: {
          title: {
            text: '',
          },
          gridLineColor: '#eee',
          gridLineDashStyle: 'Dot',
          gridLineWidth: 2,
          labels: {
            formatter() {
              // OLD FUNCTION
              // return '$'+ Highcharts.numberFormat(this.value, 0);

              // Formats the return value to a shortened version (ex. $1.5M, $340K)
              if ((this as any).value >= 1000000) {
                return `$${((this as any).value / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
              }
              if ((this as any).value >= 1000) {
                return `$${((this as any).value / 1000).toFixed(1).replace(/\.0$/, '')}K`;
              }
            },
          },
          plotLines: [
            {
              value: 0,
              width: 2,
              color: 'white',
              zIndex: 100,
            },
          ],
        },
        plotOptions: {
          area: {
            lineWidth: 0,
            marker: {
              enabled: false,
              symbol: 'circle',
              radius: 2,
              fillColor: 'white',
              lineColor: '#5FBFE2',
              states: {
                hover: {
                  enabled: true,
                },
              },
            },
          },
        },
        series: [
          {
            name: 'Sample Plan',
            color: '#DCF6FF',
            fillOpacity: 1,
            data: proposedPlan.data,
          },
          {
            name: 'Current Plan',
            color: '#FFAE3A',
            fillOpacity: 1,
            data: currentPlan.data.map((currentPlanValue, index) => {
              if (index === chartData.Categories.indexOf(retirementAge.toString())) {
                return {
                  y: currentPlanValue,
                  dataLabels: totalSavingsAtRetirementDataLabel,
                };
              }
              return currentPlanValue;
            }),
          },
        ],
        tooltip: {
          shadow: false,
          backgroundColor: 'white',
          borderColor: '#999',
          borderRadius: 5,
          formatter() {
            return `<b>${(this as any).series.name}</b><br>Age ${
              (this as any).x
            } / $${ADV.util.number_format((this as any).y)}`;
          },
        },
      }}
    />
  );
};

export default RetirementChart;
